// eslint-disable-next-line
import util from 'util';

import React from 'react';

import {withStyles} from '@material-ui/core/styles';
import * as MuiCore from '@material-ui/core';
import { InputAdornment } from '@material-ui/core';
import PaymentButton from './PaymentButton';
import PaymentCardForm from './PaymentCardForm';
import * as Stripe from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import EmailValidator from 'email-validator';
import * as PortalActions from './PortalActions';
import * as DataFormats from './DataFormats';
import AutoPayPrompt from "./AutoPayPrompt";
import Autocomplete from '@material-ui/lab/Autocomplete';

import {
    Checkbox,
    Link,
    Dialog,
    DialogActions,
    Button,
    DialogTitle,
    DialogContent,
    DialogContentText,
    TextField,
} from "@material-ui/core";

import cookie from 'cookie';
import {makeForm} from "./PortalApi";

const styles = theme => ({
    contentPanel: {
        flexShrink: 1,
        margin: '3%',
        padding: 20,
        borderRadius: 10,
        maxWidth: '800px',
    },
    formTitle: {
        ...theme.typography.body2,
        fontWeight: 'bold',
        textAlign: 'center',
        paddingBottom: 10,
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'start',
    },
    loadingText: {
        ...theme.typography.h6,
    },
    balance: {
        ...theme.typography.h6,
        color: theme.palette.secondary.main,
        display: 'flex',
        alignItems: 'center',
        paddingBottom: 16,
        borderBottom: '1px solid rgba(0, 0, 0, 0.54)',
    },
    paymentButton: {
        flexGrow: 1,
        marginLeft: 10,
    },
    payNowBlurb: {
        ...theme.typography.body2,
    },
    underlineBox: {
        borderBottom: '1px solid rgba(0, 0, 0, 0.54)',
        paddingBottom: 12,
    },
    generalText: {
        ...theme.typography.body2,
    },
    cardNumber: {
        ...theme.typography.body2,
    },
});

class Payments extends React.Component {

    /**
     * Payment Surcharge
     * @typedef {Object} PaymentSurcharge
     * @property {string} surchargeText - The prompt for the client
     * @property {int} surchargeAmount - The amount of the actual surcharge
     * @property {int} surchargePercent - The amount of the actual surcharge
     * @property {int} totalAmount - The amount of the actual surcharge
     * @property {string} funding - Credit, debit, prepaid, etc.
     * @property {string} methodType - CREDIT, ACH, ACHC, etc.
     * @property {boolean} fetched - Has this been fetched yet?
     * @property {string} [surchargeExemptStatus='NON_EXEMPT'] - The account status
     * @property {boolean} [receivedConsent=false] - Did they agree?
     * @property {boolean} [displayed=false] - Has this been displayed yet?
     *
     */

    /* Initial state when first loading */
    initialState = {
        publicKey: undefined,
        paymentAccounts: [],
        receiptEmail: null,
        emailReceipt: true,
        webUserId: undefined,
        paymentProcessorId: this.props.paymentProcessorId.trim(),
        cdnScript: this.props.cdnScript,
        loadingKey: false,
        loadingAccounts: false,
        selectedAccount: {},
        selectedPaymentMethodId: 'CARD',
        selectedPaymentMethod: {},
        paymentProcessorSpecific: undefined,
        amount: 0,
        amountChanged: false,
        payNowEnabled: false,
        errorMessage: '',
        showError: false,
        showUserError: false,
        showSuccess: false,
        errorTitle: '',
        showAddAchSuccess: false,
        showVerifyAchSuccess: false,
        showAddCardSuccess: false,
        accountType: 'business',
        accountHolderName: '',
        routingNumber: '',
        routingNumberValid: false,
        transitNumber: '',
        transitNumberValid: false,
        accountNumber: '',
        accountNumberValid: false,
        accountNumber2: '',
        accountNumber2Valid: false,
        submitting: false,
        submittingText: '',
        depositAmount1: '',
        depositAmount2: '',
        descriptorCode: '',
        achVerifyAutoPayStatus: true,
        autoPayEmail: "",
        authorizeBankAccount: false,
        showConfirmPayment: false,
        showAddingCard: false,
        completePayment: null,
        completeAddCard: null,
        autoPayExisting: false,
        addAutoPay: false,
        showAutoPayPrompt: false,
        referralUrl: null,
        showReferralUrl: false,
        showAutoPayPromptWindow: false,
        showAutoPayPromptNewPaymentWindow: false,
        showChildBCTWindow: false,
        closeAutoPaySuccess: false,
        padSingleUse: false,
        hasAchAutoPayEnabled: false,
        /** @property {PaymentSurcharge} */
        paymentSurcharge: {
            surchargeText: '',
            surchargeAmount: 0,
            surchargePercent: 0.00,
            totalAmount: 0,
            fetched: false,
            funding: '',
            methodType: '',
            surchargeExemptStatus: 'NON_EXEMPT',
            receivedConsent: false,
            displayed: false
        },
        version: 'v130',
    };

    /* Data entry fields to clear after finishing a bank operation.  The
       credit card fields are handled in the PaymentCardForm class */
    resetState = {
        accountHolderName: '',
        routingNumber: '',
        routingNumberValid: false,
        transitNumber: '',
        transitNumberValid: false,
        accountNumber: '',
        accountNumberValid: false,
        accountNumber2: '',
        accountNumber2Valid: false,
        depositAmount1: '',
        depositAmount2: '',
        descriptorCode: '',
        achVerifyAutoPayStatus: true,
        authorizeBankAccount: false,
        showAutoPayPrompt: false,
        showAutoPayPromptWindow: false,
        showAutoPayPromptNewPaymentWindow: false,
        hasAchAutoPayEnabled: false,
        padSingleUse: false,
        /** @property {PaymentSurcharge} */
        paymentSurcharge: {
            surchargeText: '',
            surchargeAmount: 0,
            surchargePercent: 0.00,
            totalAmount: 0,
            fetched: false,
            funding: '',
            methodType: '',
            surchargeExemptStatus: 'NON_EXEMPT',
            receivedConsent: false,
            displayed: false
        },
        showReferralUrl: false,
        showUserError: false,
    }

    constructor(props) {
        super(props);
        this.state = {...this.initialState};
        this.stripePromise = loadStripe(this.props.apiKey);
    }

    /* Check for any local state that needs an update */
    checkUpdate() {
        /* Clear out any information if not logged in */
        if (!Boolean(this.props.webUser.id)) {
            if (this.state.paymentAccounts.length > 0) {
                this.setState(this.initalState);
            }
            return;
        }

        /* Get the Stripe public key */
        if (!Boolean(this.state.publicKey) && !this.state.loadingKey) {
            this.setState({loadingKey: true});
            PortalActions.queryStripePublicKey((publicKey) => {
                this.setState({loadingKey: false});
                this.setState({publicKey});
            });
        }

        /* Load the payment accounts */
        if (this.state.webUserId !== this.props.webUser.id && !this.state.loadingAccounts) {
            this.setState({webUserId: this.props.webUser.id});
            this.loadPaymentAccounts();
            if (this.state.paymentAccounts.length > 0) {
                this.setInitialAmount(this.state.paymentAccounts[0].id);
            }
        }

        /* Set the receipt email if not set */
        if (Boolean(this.props.webUser) &&
            this.state.receiptEmail === null &&
            Boolean(this.props.webUser.email)) {
            this.setState({receiptEmail: this.props.webUser.email});
        }
    }

    componentDidMount() {
        /* If the script doesn't already exist, add it to the <HEAD> */
        if (this.state.cdnScript != null) {
            let elementExists = document.getElementById("ZohoCDNScript-PTN")
            if (elementExists) {
                elementExists.remove();
            }
            //Append it to head
            const pageScript = document.createElement("script");
            pageScript.id = "ZohoCDNScript-PTN";
            pageScript.type = "text/javascript";
            pageScript.async = true;
            pageScript.setAttribute("src", this.state.cdnScript);
            document.head.appendChild(pageScript);
        }
        this.checkUpdate();
    }

    componentDidUpdate() {
        this.checkUpdate();
    }

    /*
     *
     */
    queryPaymentSurcharge(payMethod) {
        let acctId = 0;

        if (payMethod && payMethod.accountId) {
            acctId = payMethod.accountId;
        } else if (this.state.selectedAccount && this.state.selectedAccount.accountId) {
            acctId = this.state.selectedAccount.accountId;
        } else if (this.state.selectedAccount && this.state.selectedAccount.id) {
            acctId = this.state.selectedAccount.id;
        }

        let amount = this.state.amount;
        let myMethod = payMethod;
        let methodType = "";
        let funding = "";
        if (myMethod && myMethod.card) {
            funding = payMethod.card.funding;
        } else if (myMethod && myMethod.methodType && myMethod.methodType === 'ACH') {
            funding = "ACH";
        } else {
            funding = payMethod.funding;
        }

        if (payMethod && payMethod.type) {
            methodType = payMethod.type;
        } else if (payMethod && payMethod.methodType) {
            methodType = payMethod.methodType;
        }

        PortalActions.queryPaymentSurcharge((surchargeResponse) => {
            let obj = this.state.paymentSurcharge;

            obj.surchargeExemptStatus = surchargeResponse.surchargeExemptStatus;
            obj.surchargeText = surchargeResponse.surchargeText;
            obj.totalAmount = surchargeResponse.totalAmount;
            obj.surchargePercent = surchargeResponse.surchargePercent
            if (surchargeResponse.surchargeAmount) {
                obj.surchargeAmount = Number(surchargeResponse.surchargeAmount);
                if (typeof (obj.surchargeAmount) === 'number') {
                    obj.surchargeAmount = obj.surchargeAmount.toFixed(2);
                }
            } else {
                obj.surchargeAmount = null;
            }

            //From the above function.
            obj.funding = funding;
            obj.methodType = methodType;
            obj.fetched = true;
            obj.receivedConsent = false;
            obj.displayed = false;

            //Set it
            this.setState({showConfirmPayment: true, paymentSurcharge: obj});
        }, () => {
            this.setState({
                showError: true,
                error: 'Error - could not retrieve surcharge calculation for payment.'
            });
        }, acctId, amount, methodType, funding);

    }

    /*
     * Validate the ACH numbers whenever they change
     */
    validateAch(routing, account, account2, transit) {
        let routingNumberValid = true;
        let transitNumberValid = true;
        let accountNumberValid = true;
        let accountNumber2Valid = true;

        if (this.state.selectedPaymentProcessor === 'STRIPE_US') {
            /* Routing numbers always 9 digits */
            if (routing.length !== 9) {
                routingNumberValid = false;
            } else {
                /* Check digit weighted sum.  Should always be an even 10 */
                let sum = 3 * (Number(routing[0]) + Number(routing[3]) + Number(routing[6])) +
                    7 * (Number(routing[1]) + Number(routing[4]) + Number(routing[7])) +
                    (Number(routing[2]) + Number(routing[5]) + Number(routing[8]));
                if (sum % 10 !== 0) {
                    routingNumberValid = false;
                }
            }

            /* No rules around account numbers except not more than 17 digits */
            if (account.length === 0 || account.length > 17) {
                accountNumberValid = false;
            }

            /* Account number verification just has to match the first */
            if (account2 !== account) {
                accountNumber2Valid = false;
            }

            this.setState({
                routingNumber: routing,
                accountNumber: account,
                accountNumber2: account2,
                routingNumberValid,
                accountNumberValid,
                accountNumber2Valid
            });
        } else if (this.state.selectedPaymentProcessor === 'STRIPE_CA') {
            /* Institution numbers (held in "routingNumber") always 3 digits */
            if (routing.length !== 3) {
                routingNumberValid = false;
            }
            /* Transit numbers always 5 digits */
            if (transit.length !== 5) {
                transitNumberValid = false;
            }

            /* It seems account numbers should be between 7 and 12 digits long */
            if (account.length < 7 || account.length > 12) {
                accountNumberValid = false;
            }

            /* Account number verification just has to match the first */
            if (account2 !== account) {
                accountNumber2Valid = false;
            }

            this.setState({
                routingNumber: routing,
                transitNumber: transit,
                accountNumber: account,
                accountNumber2: account2,
                routingNumberValid,
                transitNumberValid,
                accountNumberValid,
                accountNumber2Valid
            });
        }
    }

    routingNumberChanged(routingNumber) {
        this.validateAch(routingNumber.replace(/\D/g, ''),
            this.state.accountNumber,
            this.state.accountNumber2,
            this.state.transitNumber);

    }

    accountNumberChanged(accountNumber) {
        this.validateAch(this.state.routingNumber,
            accountNumber.replace(/\D/g, ''),
            this.state.accountNumber2,
            this.state.transitNumber);
    }

    accountNumber2Changed(accountNumber2) {
        this.validateAch(this.state.routingNumber,
            this.state.accountNumber,
            accountNumber2.replace(/\D/g, ''),
            this.state.transitNumber);

    }

    transitNumberChanged(transitNumber) {
        this.validateAch(this.state.routingNumber,
            this.state.accountNumber,
            this.state.accountNumber2,
            transitNumber.replace(/\D/g, ''));

    }

    accountSelected = (accountId, manual) => {
        let manuallySelected = false;
        if (manual) {
            manuallySelected = true;
        }
        let selectedAccount = this.state.paymentAccounts.find(account => account.id === Number(accountId))
        let selectedPaymentMethod = {};
        let selectedPaymentMethodId = this.state.selectedPaymentMethodId === 'ACHCI' ? 'ACHCI' : 'CARD';
        let selectedPaymentProcessor = selectedAccount.paymentProcessorSpecific;
        let referralUrl = undefined;
        if (selectedAccount.bctChildAccount === "Y") {
            if (this.state.paymentAccounts.length === 1 && selectedAccount.bctChildAccount === "Y") {
                this.setState({
                    showError: true, showUserError: true,
                    errorTitle: selectedAccount.accountName + " " + selectedAccount.accountNumber,
                    errorMessage: 'This account has a parent account that all billing must be done through. ' +
                        'This account cannot accept payments itself.' +
                        'You do not have any valid accounts attached to your login to make payments on.'
                });
                return;
            }

            this.setState({
                showError: true, showUserError: true,
                errorTitle: selectedAccount.accountName + " " + selectedAccount.accountNumber,
                errorMessage: 'This account has a parent account that all billing must be done through. This account cannot accept payments itself.'
            });
            return;
        }
        localStorage.setItem("lastSelectedAccountId", accountId);
        /*
            Before we do anything, ensure the payment processor ids match.
            If they do not match, there's no point in continuing as the APIKeys will not work.
         */
        if (!this.state.loadingAccounts && !this.state.loadingKey && Boolean(this.state.publicKey)) {
            if (this.state.paymentProcessorId.trim() !== selectedPaymentProcessor.trim()) {
                //Show them the referral URL and offer to take them there in a new tab.
                PortalActions.queryPaymentProcessorReferralUrl((referralUrl) => {
                    if (!referralUrl) {
                        this.setState({
                            showError: true,
                            error: 'Error loading this account - please try again in a few moments.'
                        });
                    }
                    this.setState({
                        referralUrl: referralUrl + '/portalApi/transfer',
                        showReferralUrl: true, selectedAccount
                    });
                }, () => {
                    this.setState({
                        showError: true,
                        error: 'FAIL Error loading this account - please try again in a few moments.'
                    });
                }, selectedPaymentProcessor.trim());
                return;
            }
        }

        let amount = (selectedAccount.balance || 0).toFixed(2);
        if (amount < 0) {
            amount = 0;
        }


        /* Auto select the first payment method that isn't an ACH Credit */
        let autoSelect = selectedAccount.paymentMethods.find(method => method.methodType !== 'ACHC');
        if (Boolean(autoSelect) && this.state.selectedPaymentMethodId !== 'ACHCI') {
            selectedPaymentMethod = autoSelect;
            selectedPaymentMethodId = autoSelect.id;
        }

        this.setState({
            selectedAccount,
            selectedPaymentMethod,
            selectedPaymentMethodId,
            amount,
            selectedPaymentProcessor,
            referralUrl,
            showReferralUrl: false,
            amountChanged: false,
            paymentSurcharge: {
                surchargeText: '',
                surchargeAmount: 0,
                surchargePercent: 0.00,
                totalAmount: 0,
                fetched: false,
                funding: '',
                methodType: '',
                surchargeExemptStatus: 'NON_EXEMPT',
                receivedConsent: false,
                displayed: false
            }
        });

    }

    methodSelected = (selectedPaymentMethodId) => {
        let selectedPaymentMethod = {};
        if (selectedPaymentMethodId !== 'ACH' && selectedPaymentMethodId !== 'CARD' && selectedPaymentMethodId !== 'ACHCI') {
            selectedPaymentMethod = this.state.selectedAccount.paymentMethods
                .find(method => method.id === Number(selectedPaymentMethodId));
        }
        this.setState({
            selectedPaymentMethod,
            selectedPaymentMethodId,
            padSingleUse: false,
            paymentSurcharge: {
                surchargeText: '',
                surchargeAmount: 0,
                surchargePercent: 0.00,
                totalAmount: 0,
                fetched: false,
                funding: '',
                methodType: '',
                surchargeExemptStatus: 'NON_EXEMPT',
                receivedConsent: false,
                displayed: false
            }});
    }

    canEnableAutoPay = () => {
        let method = this.state.selectedPaymentMethod;
        let id = this.state.selectedPaymentMethodId;
        if (id === 'ACHCI') {
            return false;
        }
        if (!method) {
            return false;
        }
        if ((method?.singleTimeUseFl ?? "N") === "Y") {
            return false;
        }
        if (this.state.padSingleUse) {
            return false;
        }
        //Weird edge case with non-single-time-use flags for ACH where autopay isn't allowed because they're not verified
        //But they can set up autopay
        if ((method?.methodType ?? "CARD") === "ACH") {
            if (method?.canHaveAutoPay === "N" && method?.verified === "N") {
                return true;
            }
        }
        return (method?.canHaveAutoPay ?? "Y") === "Y";
    }

    findAccountToLoad = (paymentAccounts) => {
        let badAccounts = [];
        let firstGoodAccount = null;
        let lastSelectedAccount = localStorage.getItem("lastSelectedAccountId");


        if (paymentAccounts.length > 0) {
            //Get a list of all accounts we don't want to allow to be auto-selected. manual selection is OK.
            paymentAccounts.forEach(function (item, index) {
                if (item.compatiblePaymentProcessorFl === "N") {
                    badAccounts.push(item.id);
                } else if (firstGoodAccount === null) {
                    firstGoodAccount = item.id;
                }
            });

            this.setState({paymentAccounts});
            if (this.props.transferAccountId) {
                if (!badAccounts.includes(this.props.transferAccountId)) {
                    this.accountSelected(this.props.transferAccountId, false);
                } else if (firstGoodAccount !== null) {
                    this.accountSelected(firstGoodAccount, false);
                    // this.setState({paymentAccounts, selectedAccountId: firstGoodAccount});
                } else {
                    this.setState({paymentAccounts, selectedAccountId: ''});
                }
            } else if (lastSelectedAccount) {
                try {
                    this.accountSelected(lastSelectedAccount, false);
                } catch (e) {
                    this.accountSelected(paymentAccounts[0].id, false);
                }
            } else if (firstGoodAccount !== null) {
                this.accountSelected(firstGoodAccount, false);
                // this.setState({paymentAccounts, selectedAccountId: firstGoodAccount});
            } else {
                this.accountSelected(paymentAccounts[0].id, false);
            }
        } else {
            this.setState({paymentAccounts, selectedAccountId: ''});
        }
    }

    /*
     * Load/Refresh the payment accounts list
     */
    loadPaymentAccounts = () => {
        this.setState({loadingAccounts: true});
        let webUserId = this.props.webUser.id;

        PortalActions.queryPaymentAccounts((status, paymentAccounts) => {
            this.setState({loadingAccounts: false});
            if (this.props.webUser.id === webUserId) {
                this.findAccountToLoad(paymentAccounts);
            }
        }, (status, content, message) => {
            this.setState({loadingAccounts: false});
        });
    }


    /* Called when a payment is submitted with the pay now button */
    payItNow = (complete, dollarAmount, token) => {
        let amount = Number(dollarAmount) * 100;
        PortalActions.submitTokenPayment(() => {
                complete('success');
                this.setState({showSuccess: true});
            }, (error) => {
                this.setState({showError: true, errorMessage: error});
                complete('fail');
            }, this.state.selectedAccount.id, token.id, amount, 'N',
            this.state.emailReceipt ? this.state.receiptEmail : undefined,
            this.state.paymentSurcharge,
            this.state.selectedPaymentMethod.autoPayStatus,
            this.state.selectedPaymentMethod.autoPayEmail);
    }

    closeSuccess = () => {
        this.setState({showSuccess: false});
        this.loadPaymentAccounts();
    }
    showAutoPayPrompt = () => {
        let autoBool = false;

        if (this.state.selectedPaymentMethod.autoPayFl === 'Y') {
            autoBool = true;
        }

        this.setState({autoPayExisting: autoBool, addAutoPay: autoBool, showAutoPayPromptWindow: true, showAutoPayPromptNewPaymentWindow: false});
    }
    showAutoPayPromptNewPaymentMethod = () => {
        let autoBool = false;

        if (this.state.selectedPaymentMethod.autoPayFl === 'Y') {
            autoBool = true;
        }

        this.setState({autoPayExisting: autoBool, addAutoPay: autoBool, showAutoPayPromptWindow: true, showAutoPayPromptNewPaymentWindow: true});
    }


    buildNavigationToReferralUrl = () => {
        let accountId = this.state.selectedAccount.id;
        let sessionId = localStorage.getItem("sessionKey");
        let fullUrl = this.state.referralUrl + '?' + makeForm({accountId, sessionId});

        return fullUrl;
    }

    closeAddAchSuccess = () => {
        this.setState({showAddCardSuccess: false, showAddAchSuccess: false});
        this.loadPaymentAccounts();
    }

    closeReferralUrl = () => {
        this.setState({referralUrl: null, showReferralUrl: false});
        this.loadPaymentAccounts();
    }

    closeAddCardSuccess = () => {
        this.setState({showAddCardSuccess: false, showAddAchSuccess: false});
        this.loadPaymentAccounts();
    }

    closeVerifyAchSuccess = () => {
        this.setState({showVerifyAchSuccess: false});
        this.loadPaymentAccounts();
    }
    closeAutoPayActionDismiss = () => {
        this.setState({closeAutoPaySuccess: false, showAutoPayPromptWindow: false, showAutoPayPromptNewPaymentWindow: false});
    }
    closeAutoPayActionSuccess = () => {
        this.setState({closeAutoPaySuccess: false, showAutoPayPromptWindow: false, showAutoPayPromptNewPaymentWindow: false});
        this.loadPaymentAccounts();
    }

    amountChanged(amount) {
        if (isNaN(Number(amount))) {
            return;
        }
        /* Remove any extra decimal places */
        let cleaned = amount.replace(/^(\d*\.?\d{0,2})\d*$/, '$1');
        this.setState({amount: cleaned, amountChanged: true});
    }

    /**
     * Used to locate the amount to be auto-entered in the event no account is selected
     * Or the accountSelected does not have a balance entered
     * Is ignored if the amount has been changed since the last account was selected.
     * @param selectedAccountId
     */
    setInitialAmount(selectedAccountId) {
        let selectedAccount = this.state.paymentAccounts.find(account => account.id === Number(selectedAccountId))
        let cleanedAmount = 0;

        if (selectedAccount === undefined) {
            cleanedAmount = 0;
        } else {
            if (!this.state.amountChanged) {
                if (selectedAccount.balance > 0) {
                    cleanedAmount = (selectedAccount.balance || 0).toFixed(2);
                } else {
                    cleanedAmount = 0;
                }
            } else {
                cleanedAmount = this.state.amount;
            }
        }
        this.setState({amount: cleanedAmount});
    }

    clearForm = () => {
        this.setState(this.resetState);
    }


    submit = async () => {
        /*
         * Submit request for ACH credit instructions
         */
        if (this.state.selectedPaymentMethodId === 'ACHCI') {
            this.setState({submitting: true, submittingText: 'Requesting Instructions'});
            PortalActions.addAchCreditInstructions(() => {
                this.setState({submitting: false});
                this.loadPaymentAccounts();
            }, (error, message) => {
                this.setState({submitting: false, showError: true, errorMessage: message});
            }, this.state.selectedAccount.id);
            return;
        }

        /* Submit a new bank account.  No payment as it has to be
           confirmed first */
        if (this.state.selectedPaymentMethodId === 'ACH') {
            this.setState({submitting: true, submittingText: this.state.selectedPaymentProcessor === 'STRIPE_CA' ? 'Setting up Pre-Authorized Debit Account' : 'Setting Up Bank Account'});
            PortalActions.addAchDebit(() => {
                    this.setState({submitting: false, showAddAchSuccess: true});
                    this.clearForm();
                }, (error, message) => {
                    this.setState({submitting: false, showError: true, errorMessage: message});
                }, this.state.selectedAccount.id, this.state.accountHolderName,
                this.state.accountType, this.state.routingNumber,
                this.state.accountNumber, this.state.transitNumber, this.state.padSingleUse,
                this.state.receiptEmail);
            return;
        }

        /*
         * Submit a new card with payment
         */
        if (this.state.selectedPaymentMethodId === 'CARD') {
            let paymentMethod = await this.state.getPaymentMethod();
            if (paymentMethod.error) {
                this.setState({
                    showError: true,
                    errorMessage: paymentMethod.error.message
                });
                return;
            }

            //Query surcharge!
            this.queryPaymentSurcharge(paymentMethod.paymentMethod);

            let amountFieldEmpty = !Number(this.state.amount);
            let NumberGreaterThanZero = Number(this.state.amount) > 0;
            let saveTheCard = paymentMethod.saveCard ? 'Y' : 'N';

            if (this.state.selectedAccount.paymentMethodsCount > 0) {
                saveTheCard = 'Y';
            }
            if (amountFieldEmpty || (!amountFieldEmpty && NumberGreaterThanZero)) {
                if (saveTheCard === 'Y') {
                    if (Number(this.state.amount) === 0.00) {
                        this.setState({
                            submitting: false,
                            showConfirmPayment: false,
                            showAddingCard: true,
                            showUserError: false,
                            addingPaymentMethod: paymentMethod
                        });
                        return;
                    }
                } else {
                    if (Number(this.state.amount) === 0.00) {
                        this.setState({
                            submitting: false,
                            showConfirmPayment: false,
                            showError: true,
                            showUserError: true,
                            errorMessage: "If you're adding a new card, you must either check the box to save the card, or enter an amount to make a payment of."
                        });
                        return;
                    }
                }
            }

            this.setState({
                showConfirmPayment: false,
                showUserError: false,
                amount: String(Number(this.state.amount).toFixed(2)),
                confirmPaymentMethod: paymentMethod.paymentMethod.card.brand.toUpperCase() + ' ****' +
                    paymentMethod.paymentMethod.card.last4,
                completePayment: async () => {
                    this.setState({submitting: true, submittingText: 'Submitting Payment'});
                    let amount = Number(this.state.amount) * 100;
                    if (paymentMethod.error) {
                        this.setState({
                            submitting: false, showError: true,
                            errorMessage: paymentMethod.error.message
                        });
                        return;
                    }
                    PortalActions.submitTokenPayment(() => {
                            this.setState({showSuccess: true, submitting: false, showError: false, showUserError: false});
                            paymentMethod.clearForm();
                        }, (error, message) => {
                            this.setState({submitting: false, showError: true, errorMessage: message});
                        }, this.state.selectedAccount.id, paymentMethod.paymentMethod.id,
                        amount, saveTheCard,
                        this.state.emailReceipt ? this.state.receiptEmail : undefined, this.state.paymentSurcharge,
                        paymentMethod.autoPayStatus, paymentMethod.autoPayEmail);
                }
            });
            return;
        }

        /*
         * Submit a verification for bank account
         */
        if (this.state.selectedPaymentMethod.methodType === 'ACH' &&
            this.state.selectedPaymentMethod.verified === 'N') {
            this.setState({submitting: true, submittingText: 'Verifying Bank Account'});
            let autopayEnable = false;
            if (this.canEnableAutoPay()) {
                autopayEnable = (typeof(this.state.achVerifyAutoPayStatus) === 'boolean') ? this.state.achVerifyAutoPayStatus : this.state.achVerifyAutoPayStatus === 'Y';
            } else {
                autopayEnable = false;
            }

            PortalActions.verifyAchDebit(() => {
                this.setState({submitting: false, showVerifyAchSuccess: true});
                this.clearForm();
            }, (error, message) => {
                this.setState({submitting: false, showError: true, errorMessage: message});
            }, this.state.selectedPaymentMethodId,
                this.state.depositAmount1, this.state.depositAmount2,
                this.state.descriptorCode.length > 0 ? "SM" + this.state.descriptorCode.toUpperCase() : '', autopayEnable,
                this.state.autoPayEmail);
        }

        /*
         * Payment on existing bank account
         */
        if (this.state.selectedPaymentMethod.methodType === 'ACH' &&
            this.state.selectedPaymentMethod.verified === 'Y') {
            let paymentMethod = this.state.selectedPaymentMethod;

            //Query surcharge!
            this.queryPaymentSurcharge(paymentMethod);
            this.setState({
                showConfirmPayment: true,
                confirmPaymentMethod: this.state.selectedPaymentMethod.bankName + ' ****' +
                    this.state.selectedPaymentMethod.maskedValue,
                amount: String(Number(this.state.amount).toFixed(2)),
                completePayment: async () => {
                    this.setState({submitting: true, submittingText: 'Submitting Payment'});
                    let amount = Number(this.state.amount) * 100;
                    PortalActions.submitMethodPayment(() => {
                            this.setState({showSuccess: true, submitting: false});
                        }, (error, message) => {
                            this.setState({submitting: false, showError: true, errorMessage: message});
                        }, this.state.selectedPaymentMethod.id, amount,
                        this.state.emailReceipt ? this.state.receiptEmail : undefined, this.state.paymentSurcharge);
                }
            });
            return;
        }

        /*
         * Payment on existing credit card
         */
        if (this.state.selectedPaymentMethod.methodType === 'CARD') {
            let paymentMethod = this.state.selectedPaymentMethod;


            //Query surcharge!
            this.queryPaymentSurcharge(paymentMethod);

            this.setState({
                showConfirmPayment: true,
                amount: String(Number(this.state.amount).toFixed(2)),
                confirmPaymentMethod: this.state.selectedPaymentMethod.cardBrand.toUpperCase() + ' ****' +
                    this.state.selectedPaymentMethod.maskedValue,
                completePayment: async () => {
                    this.setState({submitting: true, submittingText: 'Submitting Payment'});
                    let amount = Number(this.state.amount) * 100;
                    PortalActions.submitMethodPayment(() => {
                            this.setState({showSuccess: true, submitting: false});
                        }, (error, message) => {
                            this.setState({submitting: false, showError: true, errorMessage: message});
                        }, this.state.selectedPaymentMethod.id, amount,
                        this.state.emailReceipt ? this.state.receiptEmail : undefined, this.state.paymentSurcharge);
                }
            });
        }
    }

    /*
     * Called by the confirmation dialog when completing a payment
     */
    completeSubmitSurchargeNonExempt = () => {
        let obj = this.state.paymentSurcharge;
        obj.receivedConsent = true;
        obj.displayed = true;
        this.setState({showConfirmPayment: false, paymentSurcharge: obj});
        this.state.completePayment();
    }

    completeSubmitSurchargeExempt = () => {
        let obj = this.state.paymentSurcharge;
        obj.displayed = false;
        obj.receivedConsent = false;
        this.setState({showConfirmPayment: false, paymentSurcharge: obj});
        this.state.completePayment();
    }

    /*
     * Called by the confirmation dialog when adding a card without making a payment.
     */
    completeAddCard = () => {
        this.setState({showAddingCard: false});

        PortalActions.addCard(() => {
                this.setState({
                    showAddCardSuccess: true,
                    showSuccess: false,
                    submitting: false,
                    showError: false,
                    showUserError: false
                });
                this.state.addingPaymentMethod.clearForm();
            },
            (error, message) => {
                this.setState({submitting: false, showError: true, errorMessage: message});
            },
            this.state.selectedAccount.id,
            this.state.addingPaymentMethod.paymentMethod.id,
            this.state.addingPaymentMethod.autoPayStatus,
            this.state.addingPaymentMethod.autoPayEmail);
    }

    //Check if there are multiple emails, if there are, validate each one individually.
    //Returns true if all emails are good, returns bad if *any* emails are bad.
    checkEmails = () => {
        if (!this.state.receiptEmail) {
            return true;
        }

        let temp = this.state.receiptEmail;
        let badEmail = false;
        temp = temp.replace(',', ';');

        let emails = temp.split(';');
        emails.forEach(email => {
            if (!EmailValidator.validate(email.trim())) {
                badEmail = true;
            }
        });

        return !badEmail;
    }

    /*
     * Called when the new card form transitions to valid.
     */
    newCardValid = (getPaymentMethod) => {
        this.setState({cardValid: true, getPaymentMethod});
    }

    /*
     * Called when the new card form transitions to invalid
     */
    newCardInvalid = () => {
        this.setState({cardValid: false});
    }

    getErrorTitle(isUserError) {
        if (isUserError) {
            return this.state.errorTitle;
        } else {
            return "Request failed";
        }
    }

    getErrorCode = (error_message, isUserError) => {
        let msg = "";
        if (error_message && error_message.indexOf(";") > 0) {
            msg = error_message.substring(0, error_message.indexOf(";"));
        } else {
            msg = error_message;
        }

        if (msg && msg.length > 0 && !isUserError) {
            msg = "Hmm, something went wrong. Please try your request again in a few moments.";
        } else if (isUserError) {
            msg = error_message;
        }
        return msg;
    }

    checkEmails = (email) => {
        if (!email) {
            return true;
        }

        let temp = email;
        let badEmail = false;
        temp = temp.replace(',', ';');

        let emails = temp.split(';');
        emails.forEach(email => {
            if (!EmailValidator.validate(email.trim())) {
            badEmail = true;
            }
        });

        return !badEmail;
    }

    render() {
        const {classes, webUser} = this.props;
        // eslint-disable-next-line
        const {
            publicKey,
            loadingAccounts,
            loadingKey,
            paymentAccounts,
            amount,
            selectedAccount,
            selectedPaymentMethodId,
            selectedPaymentMethod,
            selectedPaymentProcessor,
            paymentProcessorId
        } = this.state;

        /* Display nothing if no user */
        if (!Boolean(webUser.id)) {
            return ('');
        }

        let emailList = [];
        let chkId = 0;
        if (typeof this.state.selectedAccount.billEmailTxt === 'string' || this.state.selectedAccount.billEmailTxt instanceof String) {
            chkId++;
            emailList.push({"id": chkId, "name": this.state.selectedAccount.billEmailTxt})
        }
        if (typeof this.state.selectedAccount.autoPayReceiptEmails === 'string' || this.state.selectedAccount.autoPayReceiptEmails instanceof String) {
            chkId++;
            emailList.push({"id": chkId, "name": this.state.selectedAccount.autoPayReceiptEmails})
        }
        if (typeof this.props.webUser.email === 'string' || this.props.webUser.email instanceof String) {
            chkId++;
            emailList.push({"id": chkId, "name": this.props.webUser.email})
        }
        //Remove duplicates
        emailList = [...new Set(emailList)];

        /* Put up loading screen if still loading anything. After the
         * first load we do this on the main screen so it doesn't jump on reload */
        if (paymentAccounts.length === 0 && (loadingAccounts || loadingKey || !Boolean(publicKey))) {
            return (
                <MuiCore.Dialog open={true}>
                    <MuiCore.Grid container style={{padding: 30}} alignItems='center' wrap='nowrap' spacing={2}>
                        <MuiCore.Grid item>
                            <MuiCore.CircularProgress color='secondary'/>
                        </MuiCore.Grid>
                        <MuiCore.Grid item className={classes.loadingText}>
                            Loading Payments
                        </MuiCore.Grid>
                    </MuiCore.Grid>
                </MuiCore.Dialog>);
        }

        /* No accounts */
        if (paymentAccounts.length === 0) {
            return (
                <MuiCore.Paper className={classes.contentPanel}>
                    <MuiCore.Grid container spacing={2}>

                        <MuiCore.Grid item xs={12}
                                      className={classes.formTitle}>
                            <img alt='logo' src={require('./paytrust_logo.svg')} style={{height: 100}}/>
                            Secure Online Payment Form {this.state.version}
                        </MuiCore.Grid>

                        <MuiCore.Grid item className={classes.loadingText} xs={12} style={{textAlign: 'center'}}>
                            This login has no payment accounts.
                        </MuiCore.Grid>

                        <MuiCore.Grid item xs={12} style={{textAlign: 'right'}}>
                            <MuiCore.Button onClick={this.props.onLogout}>
                                Logout
                            </MuiCore.Button>
                        </MuiCore.Grid>
                    </MuiCore.Grid>
                </MuiCore.Paper>);
        }

        /* See if we have enough information to submit */
        let canSubmit = false;
        if (this.state.selectedPaymentMethodId === 'ACH') {
            canSubmit = this.state.routingNumberValid &&
                this.state.accountNumberValid &&
                this.state.accountNumber2Valid &&
                Boolean(this.state.accountHolderName);
        }

        if (this.state.selectedPaymentMethodId === 'ACHCI') {
            canSubmit = !Boolean(selectedAccount.stripeAchRouting);
        }

        if (this.state.selectedPaymentMethod.methodType === 'ACH' && this.state.selectedPaymentMethod.verified === 'N') {
            canSubmit = (
                (this.state.depositAmount1.length >= 1 && this.state.depositAmount2.length >= 1) ||
                (this.state.descriptorCode.length == 4)
            ) && this.state.authorizeBankAccount;
        }

        let enableAmount = false;

        if (this.state.selectedPaymentMethod.methodType === 'ACH' && this.state.selectedPaymentMethod.verified === 'Y') {
            canSubmit = Number(this.state.amount) > 0 && (!this.state.emailReceipt ||
                this.checkEmails());
            enableAmount = true;
        }

        if (this.state.selectedPaymentMethod.methodType === 'CARD' && this.state.selectedPaymentMethod.verified === 'Y') {
            canSubmit = Number(this.state.amount) > 0 && (!this.state.emailReceipt ||
                this.checkEmails());
            enableAmount = true;
        }

        let addingCardOnly = false;
        if (this.state.selectedPaymentMethodId === 'CARD') {

            canSubmit = this.state.cardValid
                || (this.state.cardValid && Number(this.state.amount) > 0
                    && (!this.state.emailReceipt || this.checkEmails()));
            enableAmount = true;
            /* If we're adding a new card, and the balance is zero, but everything else is fine, change the confirm payment box. */
            if (canSubmit && enableAmount && Number(this.state.amount) === 0) {
                addingCardOnly = true;
            }
        }
        /* if this.state.showReferralUrl is not null, that means this site is not compatible. Therefore, do not enable amount! */
        if (this.state.showReferralUrl) {
            enableAmount = false;
        }

        /* Main payment screen */
        return (
            <React.Fragment>
                <MuiCore.Dialog open={this.state.loadingAccounts}>
                    <MuiCore.Grid container style={{padding: 30}} alignItems='center' wrap='nowrap' spacing={2}>
                        <MuiCore.Grid item>
                            <MuiCore.CircularProgress color='secondary'/>
                        </MuiCore.Grid>
                        <MuiCore.Grid item className={classes.loadingText}>
                            Loading Payments
                        </MuiCore.Grid>
                    </MuiCore.Grid>
                </MuiCore.Dialog>

                <MuiCore.Dialog open={this.state.submitting}>
                    <MuiCore.Grid container style={{padding: 30}} alignItems='center' wrap='nowrap' spacing={2}>
                        <MuiCore.Grid item>
                            <MuiCore.CircularProgress color='secondary'/>
                        </MuiCore.Grid>
                        <MuiCore.Grid item className={classes.loadingText}>
                            {this.state.submittingText}
                        </MuiCore.Grid>
                    </MuiCore.Grid>
                </MuiCore.Dialog>
                {this.state.paymentSurcharge.fetched
                    && !this.state.paymentSurcharge.displayed
                    && this.state.paymentSurcharge.surchargeAmount !== null
                    && (this.state.paymentSurcharge.surchargeExemptStatus === 'NON_EXEMPT' || this.state.paymentSurcharge.surchargeExemptStatus === 'DEFERRED') &&
                    <MuiCore.Dialog open={this.state.showConfirmPayment}>
                        <MuiCore.DialogTitle>
                            Confirm Payment
                        </MuiCore.DialogTitle>
                        <MuiCore.DialogContent>
                            <table>
                                <tbody>
                                <tr>
                                    <td style={{verticalAlign: 'top'}}>Account:</td>
                                    <td><b>{selectedAccount.accountNumber} {selectedAccount.accountName}</b></td>
                                </tr>
                                <tr>
                                    <td>Amount:</td>
                                    <td><b>${this.state.amount}</b></td>
                                </tr>
                                <tr>
                                    <td>Source:</td>
                                    <td><b>{this.state.confirmPaymentMethod}</b></td>
                                </tr>
                                <tr>
                                    <td>Surcharge:</td>
                                    <td>
                                        <b>{this.state.selectedAccount.currencySymbolTxt + this.state.paymentSurcharge.surchargeAmount}</b>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{whiteSpace: 'pre-wrap'}} colSpan="2">
                                        <b>{this.state.paymentSurcharge.surchargeText}</b></td>
                                </tr>
                                </tbody>
                            </table>
                        </MuiCore.DialogContent>
                        <MuiCore.DialogActions>
                            <MuiCore.Button onClick={() => this.setState({showConfirmPayment: false})}>
                                Cancel
                            </MuiCore.Button>
                            <MuiCore.Button onClick={this.completeSubmitSurchargeNonExempt}>
                                Submit Payment
                            </MuiCore.Button>
                        </MuiCore.DialogActions>
                    </MuiCore.Dialog>
                }
                {this.state.paymentSurcharge.fetched
                    && !this.state.paymentSurcharge.displayed
                    && this.state.amount > 0
                    && (this.state.paymentSurcharge.surchargeAmount === null || (this.state.paymentSurcharge.surchargeExemptStatus !== 'NON_EXEMPT' && this.state.paymentSurcharge.surchargeExemptStatus !== 'DEFERRED')) &&
                    <MuiCore.Dialog open={this.state.showConfirmPayment}>
                        <MuiCore.DialogTitle>
                            Confirm Payment
                        </MuiCore.DialogTitle>
                        <MuiCore.DialogContent>
                            <table>
                                <tbody>
                                <tr>
                                    <td style={{verticalAlign: 'top'}}>Account:</td>
                                    <td><b>{selectedAccount.accountNumber} {selectedAccount.accountName}</b></td>
                                </tr>
                                <tr>
                                    <td>Amount:</td>
                                    <td><b>${this.state.amount}</b></td>
                                </tr>
                                <tr>
                                    <td>Source:</td>
                                    <td><b>{this.state.confirmPaymentMethod}</b></td>
                                </tr>
                                </tbody>
                            </table>
                            {this.state.selectedPaymentProcessor === 'STRIPE_CA' && this.state.selectedPaymentMethod.methodType === 'ACH' &&
                                <MuiCore.Typography variant='body1'
                                                    fontSize={12}
                                                    style={{marginTop: 20, textAlign: 'left'}}>
                                    In accordance with Rule H1 provided by the Canadian Payments Association, I have
                                    certain recourse rights if any debit does not comply with this agreement.<br/><br/>For
                                    example, I have the right to receive reimbursement for any debit that is not
                                    authorized or is not consistent with this PAD Agreement.<br/>To obtain more
                                    information on my recourse rights, I may contact my financial institution or
                                    visit <a href="https://www.payments.ca" target="_blank">www.payments.ca</a>.<br/><br/>By
                                    clicking <b>Submit Payment</b>, I am accepting these terms.
                                </MuiCore.Typography>
                            }
                        </MuiCore.DialogContent>
                        <MuiCore.DialogActions>
                            <MuiCore.Button onClick={() => this.setState({showConfirmPayment: false})}>
                                Cancel
                            </MuiCore.Button>
                            <MuiCore.Button onClick={this.completeSubmitSurchargeExempt}>
                                Submit Payment
                            </MuiCore.Button>
                        </MuiCore.DialogActions>
                    </MuiCore.Dialog>
                }
                {/*{this.state.paymentSurcharge.fetched && !this.state.paymentSurcharge.displayed && this.state.paymentSurcharge.surchargeExemptStatus !== 'NON_EXEMPT' &&*/}
                {/*    <MuiCore.DialogActions>*/}
                {/*        <MuiCore.Button onClick={() => this.setState({showConfirmPayment: false})}>*/}
                {/*            Cancel*/}
                {/*        </MuiCore.Button>*/}
                {/*        <MuiCore.Button onClick={this.completeSubmitSurchargeNonExempt}>*/}
                {/*            Submit Payment*/}
                {/*        </MuiCore.Button>*/}
                {/*    </MuiCore.DialogActions>*/}
                {/*}*/}
                {/*{this.state.paymentSurcharge.fetched && !this.state.paymentSurcharge.displayed && this.state.paymentSurcharge.surchargeExemptStatus !== 'EXEMPT' &&*/}
                {/*    <MuiCore.DialogActions>*/}
                {/*        <MuiCore.Button onClick={() => this.setState({showConfirmPayment: false})}>*/}
                {/*            Cancel*/}
                {/*        </MuiCore.Button>*/}
                {/*        <MuiCore.Button onClick={this.completeSubmitSurchargeExempt}>*/}
                {/*            Submit Payment*/}
                {/*        </MuiCore.Button>*/}
                {/*    </MuiCore.DialogActions>*/}
                {/*}*/}

                <MuiCore.Dialog open={this.state.showAddingCard}>
                    <MuiCore.DialogTitle>
                        Confirm Adding Card to Your Account
                    </MuiCore.DialogTitle>
                    <MuiCore.DialogContent>
                        <table>
                            <tbody>
                            <tr>
                                <td style={{verticalAlign: 'top'}}>Account:</td>
                                <td><b>{selectedAccount.accountNumber} {selectedAccount.accountName}</b></td>
                            </tr>
                            </tbody>
                        </table>
                    </MuiCore.DialogContent>
                    <MuiCore.DialogActions>
                        <MuiCore.Button
                            onClick={() => this.setState({showConfirmPayment: false, showAddingCard: false})}>
                            Cancel
                        </MuiCore.Button>
                        <MuiCore.Button onClick={this.completeAddCard}>
                            Add Card
                        </MuiCore.Button>
                    </MuiCore.DialogActions>
                </MuiCore.Dialog>

                <Stripe.Elements stripe={this.stripePromise}>
                    <MuiCore.Paper className={classes.contentPanel}>
                        <MuiCore.Dialog open={this.state.showSuccess}>
                            <MuiCore.DialogContent>
                                <MuiCore.Typography variant='h6' style={{textAlign: 'center', textWeight: 'bold'}}>
                                    Thank You!
                                </MuiCore.Typography>
                                {this.state.receiptEmail &&
                                    <MuiCore.Typography variant='body1'
                                                        style={{marginTop: 20, textAlign: 'center'}}>
                                        Your payment has been successfully submitted. Your balance should update in
                                        a
                                        few
                                        minutes but may take longer if the network is busy.
                                        You should receive an email in a few moments with a receipt attached.
                                    </MuiCore.Typography>
                                }
                                {!this.state.receiptEmail &&
                                    <MuiCore.Typography variant='body1'
                                                        style={{marginTop: 20, textAlign: 'center'}}>
                                        Your payment has been successfully submitted. Your balance should update in
                                        a
                                        few
                                        minutes but may take longer if the network is busy.
                                    </MuiCore.Typography>
                                }
                            </MuiCore.DialogContent>
                            <MuiCore.DialogActions>
                                <MuiCore.Button onClick={this.closeSuccess}>
                                    OK
                                </MuiCore.Button>
                            </MuiCore.DialogActions>
                        </MuiCore.Dialog>
                        <MuiCore.Dialog open={this.state.showAddCardSuccess}>
                            <MuiCore.DialogContent>
                                <MuiCore.Typography variant='h6' style={{textAlign: 'center', textWeight: 'bold'}}>
                                    Credit Card Attached
                                </MuiCore.Typography>
                                <MuiCore.Typography variant='body1' style={{marginTop: 20, textAlign: 'center'}}>
                                    The credit card has successfully been added to your account.
                                    It may take a few moments to appear in the portal, depending on your network.
                                </MuiCore.Typography>
                            </MuiCore.DialogContent>
                            <MuiCore.DialogActions>
                                <MuiCore.Button onClick={this.closeAddCardSuccess}>
                                    OK
                                </MuiCore.Button>
                            </MuiCore.DialogActions>
                        </MuiCore.Dialog>
                        <MuiCore.Dialog open={this.state.showAddAchSuccess}>
                            <MuiCore.DialogContent>
                                <MuiCore.Typography variant='h6' style={{textAlign: 'center', textWeight: 'bold'}}>
                                    Bank Account Submitted
                                </MuiCore.Typography>
                                <MuiCore.Typography variant='body1' style={{marginTop: 20, textAlign: 'center'}}>
                                    Your bank account has been successfully submitted. When
                                    you see the verification deposit(s), return to this page,
                                    select the bank account in the payment methods list, and enter the
                                    value(s) within <b>10 calendar days</b>.
                                </MuiCore.Typography>
                            </MuiCore.DialogContent>
                            <MuiCore.DialogActions>
                                <MuiCore.Button onClick={this.closeAddAchSuccess}>
                                    OK
                                </MuiCore.Button>
                            </MuiCore.DialogActions>
                        </MuiCore.Dialog>

                        <MuiCore.Dialog open={this.state.showVerifyAchSuccess}>
                            <MuiCore.DialogContent>
                                <MuiCore.Typography variant='h6' style={{textAlign: 'center', textWeight: 'bold'}}>
                                    Bank Account Verified
                                </MuiCore.Typography>
                                <MuiCore.Typography variant='body1' style={{marginTop: 20, textAlign: 'center'}}>
                                    Your bank account has been successfully verified. It may take a few minutes
                                    for payments to be enabled.
                                </MuiCore.Typography>
                            </MuiCore.DialogContent>
                            <MuiCore.DialogActions>
                                <MuiCore.Button onClick={this.closeVerifyAchSuccess}>
                                    OK
                                </MuiCore.Button>
                            </MuiCore.DialogActions>
                        </MuiCore.Dialog>

                        <MuiCore.Dialog open={this.state.closeAutoPaySuccess}>
                            <MuiCore.DialogContent>
                                <MuiCore.Typography variant='h6' style={{textAlign: 'center', textWeight: 'bold'}}>
                                    Auto Pay Successfully Updated
                                </MuiCore.Typography>
                                <MuiCore.Typography variant='body1' style={{marginTop: 20, textAlign: 'center'}}>
                                    The auto pay status for this payment method has been updated in our systems.
                                </MuiCore.Typography>
                            </MuiCore.DialogContent>
                            <MuiCore.DialogActions>
                                <MuiCore.Button onClick={this.closeAutoPayActionSuccess}>
                                    OK
                                </MuiCore.Button>
                            </MuiCore.DialogActions>
                        </MuiCore.Dialog>

                        {/*  If the referralUrl is not null, that means the currently selected account does not belong on this payment processor
                        *  And we should guide the user to the correct payment processor.
                        */}

                        {this.state.showReferralUrl &&
                            <>
                                <DialogTitle>{this.state.selectedAccount.accountNumber} {this.state.selectedAccount.accountName}</DialogTitle>
                                <DialogContent>
                                    <DialogContentText>
                                        Oops! Payments cannot be made for this account on this site. Please click
                                        the
                                        button
                                        below to navigate to our payment site for this currency.
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                    <a href={this.buildNavigationToReferralUrl()} target="_self"
                                       style={{textDecoration: 'none'}}>
                                        <Button variant="contained" type="submit">Go to Site</Button>
                                    </a>
                                </DialogActions>
                            </>
                        }
                        <MuiCore.Dialog open={this.state.showError}>
                            <MuiCore.DialogTitle>
                                {this.getErrorTitle(this.state.showUserError)}
                                {/*Request Failed*/}
                            </MuiCore.DialogTitle>
                            <MuiCore.DialogContent>
                                {this.getErrorCode(this.state.errorMessage, this.state.showUserError)}
                            </MuiCore.DialogContent>
                            <MuiCore.DialogActions>
                                <MuiCore.Button onClick={() => this.setState({
                                    showError: false,
                                    showUserError: false,
                                    errorTitle: ''
                                })}>
                                    OK
                                </MuiCore.Button>
                            </MuiCore.DialogActions>
                        </MuiCore.Dialog>

                        <MuiCore.Grid container spacing={2}>

                            <MuiCore.Grid item xs={12} className={classes.formTitle}>
                                <img alt='logo' src={require('./paytrust_logo.svg')} style={{height: 100}}/>
                                Secure Online Payment Form {this.state.version}
                            </MuiCore.Grid>

                            <MuiCore.Grid item xs={12} style={{display: 'flex'}}>
                                <MuiCore.FormControl fullWidth>
                                    <MuiCore.InputLabel>Payment Account</MuiCore.InputLabel>
                                    <MuiCore.Select native
                                                    value={this.state.selectedAccount.id}
                                                    onChange={(event) => this.accountSelected(event.target.value, true)}>
                                        {this.state.paymentAccounts.map(account => {
                                            return (
                                                <option key={account.id} value={account.id}>
                                                    {account.accountNumber} {account.accountName},
                                                    Balance {(account.balance || 0).toFixed(2)}
                                                </option>);
                                        })}
                                    </MuiCore.Select>
                                </MuiCore.FormControl>
                            </MuiCore.Grid>
                            {!this.state.showReferralUrl &&
                                <MuiCore.Grid item xs={12} sm={8} style={{display: 'flex'}}>
                                    <MuiCore.TextField value={this.state.receiptEmail}
                                                       error={!this.checkEmails()}
                                                       onChange={(event) => this.setState({receiptEmail: event.target.value})}
                                                       fullWidth label='Receipt Email'/>
                                </MuiCore.Grid>}
                            {!this.state.showReferralUrl &&
                                <MuiCore.Grid item xs={12} sm={4} style={{display: 'flex'}}>
                                    <MuiCore.FormControlLabel style={{whiteSpace: 'nowrap'}}
                                                              control={<MuiCore.Checkbox
                                                                  onChange={(event) => this.setState({emailReceipt: event.target.checked})}
                                                                  checked={this.state.emailReceipt}/>}
                                                              label="Email Receipt"/>
                                </MuiCore.Grid>}

                            {!this.state.showReferralUrl &&
                                <MuiCore.Grid item xs={12}>
                                    <div className={classes.balance}>
                                        Balance: {(selectedAccount.balance || 0).toFixed(2)}
                                        <div className={classes.paymentButton}>
                                            <Stripe.ElementsConsumer>
                                                {({elements, stripe}) => (
                                                    <PaymentButton elements={elements}
                                                                   stripe={stripe}
                                                                   balance={selectedAccount.balance}
                                                                   paymentProcessorId={this.props.paymentProcessorId}
                                                                   onEnabled={() => this.setState({payNowEnabled: true})}
                                                                   onDisabled={() => this.setState({payNowEnabled: false})}
                                                                   onPayment={this.payItNow}/>
                                                )}
                                            </Stripe.ElementsConsumer>
                                        </div>
                                    </div>
                                </MuiCore.Grid>}
                            {!this.state.showReferralUrl &&
                                <MuiCore.Hidden xlDown={!this.state.payNowEnabled}>
                                    <MuiCore.Grid item xs={12} className={classes.payNowBlurb}>
                                        <div className={classes.underlineBox}>
                                            The pay button may be used to pay your full balance
                                            with Apple Pay, Google Pay, Microsoft Pay or other browser
                                            wallets. Use the form below for other payment types and amounts.
                                        </div>
                                    </MuiCore.Grid>
                                </MuiCore.Hidden>
                            }
                            {!this.state.showReferralUrl &&

                                <MuiCore.Grid item xs={12} sm={6}>
                                    {this.state.selectedPaymentProcessor === 'STRIPE_US' && (
                                        <MuiCore.FormControl fullWidth>
                                            <MuiCore.InputLabel>Payment Method</MuiCore.InputLabel>
                                            <MuiCore.Select native
                                                            value={selectedPaymentMethodId}
                                                            onChange={(event) => this.methodSelected(event.target.value)}>
                                                <option value='CARD'>** New Credit Card **</option>
                                                <option value='ACH'>** New Bank Account **</option>
                                                {(selectedAccount.paymentMethods || [])
                                                    .filter(method => method.methodType !== 'ACHC')
                                                    .map(method => {
                                                        let methodName = 'Unknown Type';
                                                        if (method.methodType === 'ACH') {
                                                            methodName = method.bankName + ' ' + method.maskedValue;
                                                            if (method.verified === 'N') {
                                                                methodName = methodName + ' (unverified)';
                                                            }
                                                        }
                                                        if (method.methodType === 'CARD') {
                                                            methodName = method.cardBrand.toUpperCase() + ' ****' + method.maskedValue;
                                                        }
                                                        if (method.autoPayFl === "Y") {
                                                            methodName = methodName + " (Autopay Enabled)"
                                                        }
                                                        return (
                                                            <option key={method.id} value={method.id}>
                                                                {methodName}
                                                            </option>);
                                                    })}
                                                <option value='ACHCI'>ACH Credit Transfer Instructions</option>
                                            </MuiCore.Select>
                                        </MuiCore.FormControl>
                                    )}
                                    {this.state.selectedPaymentProcessor === 'STRIPE_CA' && (
                                        <MuiCore.FormControl fullWidth>
                                            <MuiCore.InputLabel>Payment Method</MuiCore.InputLabel>
                                            <MuiCore.Select native
                                                            value={selectedPaymentMethodId}
                                                            onChange={(event) => this.methodSelected(event.target.value)}>
                                                <option value='CARD'>** New Credit Card **</option>
                                                <option value='ACH'>** New Pre-Authorized Debit (ACSS) **</option>
                                                {(selectedAccount.paymentMethods || [])
                                                    .filter(method => method.methodType !== 'ACHC')
                                                    .map(method => {
                                                        let methodName = 'Unknown Type';
                                                        if (method.methodType === 'ACH') {
                                                            methodName = method.bankName + ' ' + method.maskedValue;
                                                            if (method.verified === 'N') {
                                                                methodName = methodName + ' (unverified)';
                                                            }
                                                        }
                                                        if (method.methodType === 'CARD') {
                                                            methodName = method.cardBrand.toUpperCase() + ' ****' + method.maskedValue;
                                                        }
                                                        if (method.autoPayFl === "Y") {
                                                            methodName = methodName + " (Autopay Enabled)"
                                                        }
                                                        return (
                                                            <option key={method.id} value={method.id}>
                                                                {methodName}
                                                            </option>);
                                                    })}
                                                <option value='ACHCI'>Bank Transfer Instructions</option>
                                            </MuiCore.Select>
                                        </MuiCore.FormControl>
                                    )}
                                </MuiCore.Grid>
                            }
                            {!this.state.showReferralUrl &&
                                <MuiCore.Grid item xs={12} sm={6}>
                                    <MuiCore.TextField disabled={!enableAmount}
                                                       error={(enableAmount && !(Number(this.state.amount) > 0) && selectedPaymentMethodId !== 'CARD')}
                                                       value={this.state.amount}
                                                       onChange={(event) => this.amountChanged(event.target.value)}
                                                       fullWidth label='Amount'/>
                                </MuiCore.Grid>
                            }
                            {
                                /* ACH Credit selected.  All we do is show the routing and-->
                                   account number they can use to push payments */
                            }
                            {!this.state.showReferralUrl &&
                                <MuiCore.Hidden xlDown={!(this.state.selectedPaymentMethod.methodType === 'ACHC' && this.state.selectedPaymentProcessor === 'STRIPE_US')}>
                                    <MuiCore.Grid item xs={12} className={classes.generalText}>
                                        ACH Credit Transfer is available for U.S. banks
                                        and allows push payments from your banking software.
                                        Setup a payee with the following routing and account
                                        number.
                                    </MuiCore.Grid>


                                    <MuiCore.Grid item xs={12} className={classes.generalText}>
                                        <ul>
                                            <li>Routing Number: <b>{selectedAccount.stripeAchRouting}</b></li>
                                            <li>Account Number: <b>{selectedAccount.stripeAchAccount}</b></li>
                                        </ul>
                                    </MuiCore.Grid>

                                </MuiCore.Hidden>
                            }
                            {!this.state.showReferralUrl &&
                                <MuiCore.Hidden xlDown={!(this.state.selectedPaymentMethod.methodType === 'ACHC' && this.state.selectedPaymentProcessor === 'STRIPE_CA')}>
                                    <MuiCore.Grid item xs={12} className={classes.generalText}>
                                        Bank Transfer is available for Canadian banks
                                        and allows push payments from your banking software.
                                        Setup a payee with the following routing and account
                                        number.
                                    </MuiCore.Grid>


                                    <MuiCore.Grid item xs={12} className={classes.generalText}>
                                        <ul>
                                            <li>Routing Number: <b>{selectedAccount.stripeAchRouting}</b></li>
                                            <li>Account Number: <b>{selectedAccount.stripeAchAccount}</b></li>
                                        </ul>
                                    </MuiCore.Grid>

                                </MuiCore.Hidden>
                            }

                            {
                                /* ACH Credit Instructions selected. Show the routing and account number if populated,
                                   else prompt to use Submit button to request said instructions.
                                */
                            }
                            {!this.state.showReferralUrl &&
                                <MuiCore.Hidden xlDown={!(selectedPaymentMethodId === 'ACHCI' && this.state.selectedPaymentProcessor === 'STRIPE_US')}>
                                    <MuiCore.Grid item xs={12} className={classes.generalText}>
                                        ACH Credit Transfer is available for U.S. banks
                                        and allows push payments from your banking software.
                                        Setup a payee with the following routing and account
                                        number.
                                    </MuiCore.Grid>

                                    <MuiCore.Hidden xlDown={!Boolean(selectedAccount.stripeAchRouting)}>
                                        <MuiCore.Grid item xs={12} className={classes.generalText}>
                                            <ul>
                                                <li>Routing Number: <b>{selectedAccount.stripeAchRouting}</b></li>
                                                <li>Account Number: <b>{selectedAccount.stripeAchAccount}</b></li>
                                            </ul>
                                        </MuiCore.Grid>
                                    </MuiCore.Hidden>

                                    <MuiCore.Hidden xlDown={Boolean(selectedAccount.stripeAchRouting)}>
                                        <MuiCore.Grid item xs={12} className={classes.generalText}>
                                            <i>ACH credit instructions have not been requested for this account. Press <b>Submit</b> to request.</i>
                                        </MuiCore.Grid>
                                    </MuiCore.Hidden>

                                </MuiCore.Hidden>
                            }
                            {!this.state.showReferralUrl &&
                                <MuiCore.Hidden xlDown={!(selectedPaymentMethodId === 'ACHCI' && this.state.selectedPaymentProcessor === 'STRIPE_CA')}>
                                    <MuiCore.Grid item xs={12} className={classes.generalText}>
                                        Bank Transfer is available for Canadian banks
                                        and allows push payments from your banking software.
                                        Setup a payee with the following routing and account
                                        number.
                                    </MuiCore.Grid>

                                    <MuiCore.Hidden xlDown={!Boolean(selectedAccount.stripeAchRouting)}>
                                        <MuiCore.Grid item xs={12} className={classes.generalText}>
                                            <ul>
                                                <li>Routing Number: <b>{selectedAccount.stripeAchRouting}</b></li>
                                                <li>Account Number: <b>{selectedAccount.stripeAchAccount}</b></li>
                                            </ul>
                                        </MuiCore.Grid>
                                    </MuiCore.Hidden>

                                    <MuiCore.Hidden xlDown={Boolean(selectedAccount.stripeAchRouting)}>
                                        <MuiCore.Grid item xs={12} className={classes.generalText}>
                                            <i>Bank transfer instructions have not been requested for this account. Press <b>Submit</b> to request.</i>
                                        </MuiCore.Grid>
                                    </MuiCore.Hidden>

                                </MuiCore.Hidden>
                            }

                            {
                                /* ACH method selected but not verified. Show the verification form  */
                            }
                            {!this.state.showReferralUrl &&
                                <MuiCore.Hidden
                                    xlDown={selectedPaymentMethod.methodType !== 'ACH' || selectedPaymentMethod.verified === 'Y' || (selectedPaymentMethod?.microdepositVerificationType ?? 'AMOUNTS') !== 'AMOUNTS'}>
                                    <MuiCore.Grid item xs={12} style={{paddingTop: 10, fontWeight: 'bold'}}
                                                  className={classes.generalText}>
                                        Enter the two verification deposit amounts in cents to verify this bank
                                        account
                                        and
                                        check the box to authorize access before <b>{selectedPaymentMethod.verifyExpirationStr}</b>.
                                        The amounts must be entered in the order they appear on your bank statement.
                                        Example: if your statement shows 0.32 and 0.45 deposits, enter 32 and 45
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={6}>
                                        <MuiCore.TextField fullWidth label='Deposit Value 1'
                                                           value={this.state.depositAmount1}
                                                           onChange={(event) => {
                                                               let digits = Number(event.target.value.replace(/\D/g, ''));
                                                                   if (digits.toString().length <= 2) {
                                                                       this.setState({depositAmount1: digits.toString()});
                                                                   }
                                                           }}
                                        />
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={6}>
                                        <MuiCore.TextField fullWidth label='Deposit Value 2'
                                                           value={this.state.depositAmount2}
                                                           onChange={(event) => {
                                                               let digits = Number(event.target.value.replace(/\D/g, ''));
                                                                   if (digits.toString().length <= 2) {
                                                                       this.setState({depositAmount2: digits.toString()});
                                                                   }
                                                           }}
                                        />
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} style={{display: 'flex'}}>
                                        <MuiCore.FormControlLabel control={<MuiCore.Checkbox
                                                                  onChange={(event) => this.setState({authorizeBankAccount: event.target.checked})}
                                                                  checked={this.state.authorizeBankAccount}/>} label={
                                            <div>
                                                <MuiCore.Typography variant='body2'>
                                                    I authorize <b>PayTrustNow</b> on behalf
                                                    of <b>{selectedAccount.statementGroupName}</b> to electronically debit
                                                    my
                                                    account and, if necessary, electronically
                                                    credit my account to correct erroneous debits. This authorization may be
                                                    revoked
                                                    by calling customer service at <b
                                                    style={{whiteSpace: 'nowrap'}}>{DataFormats.phoneNumber(selectedAccount.statementGroupBillingPhone)}</b>.
                                                </MuiCore.Typography>
                                            </div>
                                            }
                                        />
                                    </MuiCore.Grid>
                                    {(this.state.authorizeBankAccount && this.canEnableAutoPay()) &&
                                        <MuiCore.Grid item xs={12}>
                                            <MuiCore.FormControlLabel control={<MuiCore.Checkbox
                                                onChange={(event) => this.setState({achVerifyAutoPayStatus: event.target.checked})}
                                                checked={this.state.achVerifyAutoPayStatus} />} label={
                                                <div>
                                                    <MuiCore.Typography variant='body2'>
                                                        I further authorize <b>{selectedAccount.statementGroupName}</b> to electronically debit my account as future payments become due.
                                                    </MuiCore.Typography>
                                                    <MuiCore.Typography variant='body2'>
                                                        (You may disable electronic payments at any time from this portal or by calling <b style={{whiteSpace: 'nowrap'}}>
                                                        {DataFormats.phoneNumber(selectedAccount.statementGroupBillingPhone)}</b>.)
                                                    </MuiCore.Typography>
                                                </div>
                                                }
                                            />
                                            {this.state.achVerifyAutoPayStatus &&
                                                <MuiCore.FormControl fullWidth>
                                                    <Autocomplete freeSolo options={emailList}
                                                                    onChange={(event, newValue) => {
                                                                    if (typeof newValue === 'object' && newValue !== null) {
                                                                        this.setState({autoPayEmail: newValue.name})
                                                                    } else if (newValue !== null) {
                                                                        this.setState({autoPayEmail: newValue})
                                                                    }
                                                                    }}
                                                                    onInputChange={(event, newValue) => {
                                                                    if (typeof newValue === 'object' && newValue !== null) {
                                                                        this.setState({autoPayEmail: newValue.name})
                                                                    } else if (newValue !== null) {
                                                                        this.setState({autoPayEmail: newValue})
                                                                    }
                                                                    }}
                                                                    inputValue={this.state.autoPayEmail}
                                                                    getOptionLabel={(option) => option.name}
                                                                    renderInput={(params) => (<TextField
                                                                    {...params}
                                                                    label="Auto Pay Receipt Emails"
                                                                    margin="normal"
                                                                    variant="outlined"
                                                                    error={!this.checkEmails(this.state.autoPayEmail)}
                                                                    inputProps={{ ...params.inputProps, maxLength: 500}}
                                                                    InputProps={{...params.InputProps, type: 'search'}}
                                                                    InputLabelProps={{ style: {backgroundColor: "white"}}}
                                                                    />)}
                                                    />
                                                </MuiCore.FormControl>
                                            }
                                        </MuiCore.Grid>
                                    }

                                </MuiCore.Hidden>
                            }
                            {!this.state.showReferralUrl &&
                                <MuiCore.Hidden
                                    xlDown={selectedPaymentMethod.methodType !== 'ACH' || selectedPaymentMethod.verified === 'Y' || selectedPaymentMethod.microdepositVerificationType != 'DESCRIPTOR_CODE'}>
                                    <MuiCore.Grid item xs={12} style={{paddingTop: 10, fontWeight: 'bold'}}
                                                  className={classes.generalText}>
                                        A small deposit has been sent to this bank account. To verify the account, please confirm
                                        the 6-digit code starting with 'SM' found in the statement descriptor of the deposit before <b>{selectedPaymentMethod.verifyExpirationStr}</b>.
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={3}>
                                        <MuiCore.TextField fullWidth label='Descriptor Code'
                                                           value={this.state.descriptorCode}
                                                           error={this.state.descriptorCode.length > 4}
                                                           onChange={(event) => {
                                                                if (event.target.value.length <= 4) {
                                                                    this.setState({descriptorCode: event.target.value});
                                                                }
                                                           }}
                                                           inputProps={{ style: { textTransform: "uppercase" } }} // force uppercase display
                                                           InputProps={{ startAdornment: <InputAdornment position="start" style={{marginBottom: "2px", marginRight: 0}}>SM</InputAdornment>}} // display forced prefix of "SM"
                                                           
                                        />
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} style={{display: 'flex'}}>
                                        <MuiCore.FormControlLabel control={<MuiCore.Checkbox
                                                                  onChange={(event) => this.setState({authorizeBankAccount: event.target.checked})}
                                                                  checked={this.state.authorizeBankAccount}/>} label={
                                            <div>
                                                <MuiCore.Typography variant='body2'>
                                                    I authorize <b>PayTrustNow</b> on behalf
                                                    of <b>{selectedAccount.statementGroupName}</b> to electronically debit
                                                    my
                                                    account and, if necessary, electronically
                                                    credit my account to correct erroneous debits. This authorization may be
                                                    revoked
                                                    by calling customer service at <b
                                                    style={{whiteSpace: 'nowrap'}}>{DataFormats.phoneNumber(selectedAccount.statementGroupBillingPhone)}</b>.
                                                </MuiCore.Typography>
                                            </div>
                                            }
                                        />
                                    </MuiCore.Grid>
                                    {(this.state.authorizeBankAccount && this.canEnableAutoPay()) &&
                                        <MuiCore.Grid item xs={12}>
                                            <MuiCore.FormControlLabel control={<MuiCore.Checkbox
                                                onChange={(event) => this.setState({achVerifyAutoPayStatus: event.target.checked})}
                                                checked={this.state.achVerifyAutoPayStatus} />} label={
                                                <div>
                                                    <MuiCore.Typography variant='body2'>
                                                        I further authorize <b>{selectedAccount.statementGroupName}</b> to electronically debit my account as future payments become due.
                                                    </MuiCore.Typography>
                                                    <MuiCore.Typography variant='body2'>
                                                        (You may disable electronic payments at any time from this portal or by calling <b style={{whiteSpace: 'nowrap'}}>
                                                        {DataFormats.phoneNumber(selectedAccount.statementGroupBillingPhone)}</b>.)
                                                    </MuiCore.Typography>
                                                </div>
                                                }
                                            />
                                            {this.state.achVerifyAutoPayStatus &&
                                                <MuiCore.FormControl fullWidth>
                                                <Autocomplete freeSolo options={emailList}
                                                                onChange={(event, newValue) => {
                                                                if (typeof newValue === 'object' && newValue !== null) {
                                                                    this.setState({autoPayEmail: newValue.name})
                                                                } else if (newValue !== null) {
                                                                    this.setState({autoPayEmail: newValue})
                                                                }
                                                                }}
                                                                onInputChange={(event, newValue) => {
                                                                if (typeof newValue === 'object' && newValue !== null) {
                                                                    this.setState({autoPayEmail: newValue.name})
                                                                } else if (newValue !== null) {
                                                                    this.setState({autoPayEmail: newValue})
                                                                }
                                                                }}
                                                                inputValue={this.state.autoPayEmail}
                                                                getOptionLabel={(option) => option.name}
                                                                renderInput={(params) => (<TextField
                                                                {...params}
                                                                label="Auto Pay Receipt Emails"
                                                                margin="normal"
                                                                variant="outlined"
                                                                error={!this.checkEmails(this.state.autoPayEmail)}
                                                                inputProps={{ ...params.inputProps, maxLength: 500}}
                                                                InputProps={{...params.InputProps, type: 'search'}}
                                                                InputLabelProps={{ style: {backgroundColor: "white"}}}
                                                                />)}
                                                />
                                                </MuiCore.FormControl>
                                            }
                                        </MuiCore.Grid>
                                    }

                                </MuiCore.Hidden>
                            }
                            {
                                /* New Bank account selected.  Show the bank account form */
                            }
                            {!this.state.showReferralUrl && this.state.selectedPaymentProcessor === 'STRIPE_US' &&
                                <MuiCore.Hidden xlDown={selectedPaymentMethodId !== 'ACH'}>
                                    <MuiCore.Grid item xs={12} style={{paddingTop: 10}}>
                                        <b>Bank account payments are only available for U.S. banks and
                                            require a verification process. This is only required the first
                                            time it is used:</b>
                                        <ol>
                                            <li>Enter the routing and account number of your account.</li>
                                            <li>In the next few days you'll either receive two unique small amounts or one $0.01 amount.</li>
                                            <ul>
                                                <li>If you received two unique amounts, you will enter those two amounts here to complete the verification.</li>
                                                <li>If you received one $0.01 amount, you will enter the code from the statement descriptor of the deposit.</li>
                                                <li>Verification must be completed within <b>10 calendar days</b> of submitting bank information.</li>
                                            </ul>
                                        </ol>
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={6}>
                                        <MuiCore.FormControl fullWidth>
                                            <MuiCore.InputLabel>Bank Account Type</MuiCore.InputLabel>
                                            <MuiCore.Select native
                                                            value={this.state.accountType}
                                                            onChange={(event) => this.setState({accountType: event.target.value})}>
                                                <option value='business'>Business</option>
                                                <option value='personal'>Personal</option>
                                            </MuiCore.Select>
                                        </MuiCore.FormControl>
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={6}>
                                        <MuiCore.TextField fullWidth label='Account Holder Name'
                                                           error={!Boolean(this.state.accountHolderName)}
                                                           value={this.state.accountHolderName}
                                                           onChange={(event) => this.setState({accountHolderName: event.target.value})}/>
                                    </MuiCore.Grid>


                                    <MuiCore.Grid item xs={12} sm={4}>
                                        <MuiCore.TextField fullWidth label='Routing #'
                                                           error={!this.state.routingNumberValid}
                                                           value={this.state.routingNumber}
                                                           onChange={(event) => this.routingNumberChanged(event.target.value)}/>
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={4}>
                                        <MuiCore.TextField fullWidth label='Account #'
                                                           error={!this.state.accountNumberValid}
                                                           value={this.state.accountNumber}
                                                           onChange={(event) => this.accountNumberChanged(event.target.value)}/>
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={4}>
                                        <MuiCore.TextField fullWidth label='Verify Account #'
                                                           error={!this.state.accountNumber2Valid}
                                                           value={this.state.accountNumber2}
                                                           onChange={(event) => this.accountNumber2Changed(event.target.value)}/>
                                    </MuiCore.Grid>

                                </MuiCore.Hidden>
                            }
                            {!this.state.showReferralUrl && this.state.selectedPaymentProcessor === 'STRIPE_CA' &&
                                <MuiCore.Hidden xlDown={selectedPaymentMethodId !== 'ACH'}>
                                    <MuiCore.Grid item xs={12} style={{paddingTop: 10}}>
                                        <b>Pre-authorized debits are only available for Canadian banks and
                                            require a verification process. This is only required the first
                                            time it is used:</b>
                                        <ol>
                                            <li>Enter the institution, transit, and account number of your account.</li>
                                            <li>In the next few days you'll either receive two unique small amounts or one $0.01 amount.</li>
                                            <ul>
                                                <li>If you received two unique amounts, you will enter those two amounts here to complete the verification.</li>
                                                <li>If you received one $0.01 amount, you will enter the code from the statement descriptor of the deposit.</li>
                                                <li>Verification must be completed within <b>10 calendar days</b> of submitting bank information.</li>
                                            </ul>
                                        </ol>
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={6}>
                                        <MuiCore.FormControl fullWidth>
                                            <MuiCore.InputLabel>Bank Account Type</MuiCore.InputLabel>
                                            <MuiCore.Select native
                                                            value={this.state.accountType}
                                                            onChange={(event) => this.setState({accountType: event.target.value})}>
                                                <option value='business'>Business</option>
                                                <option value='personal'>Personal</option>
                                            </MuiCore.Select>
                                        </MuiCore.FormControl>
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={6}>
                                        <MuiCore.TextField fullWidth label='Account Holder Name'
                                                           error={!Boolean(this.state.accountHolderName)}
                                                           value={this.state.accountHolderName}
                                                           onChange={(event) => this.setState({accountHolderName: event.target.value})}/>
                                    </MuiCore.Grid>


                                    <MuiCore.Grid item xs={12} sm={3}>
                                        <MuiCore.TextField fullWidth label='Institution #'
                                                           error={!this.state.routingNumberValid}
                                                           value={this.state.routingNumber}
                                                           onChange={(event) => this.routingNumberChanged(event.target.value)}/>
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={3}>
                                        <MuiCore.TextField fullWidth label='Transit #'
                                                           error={!this.state.transitNumberValid}
                                                           value={this.state.transitNumber}
                                                           onChange={(event) => this.transitNumberChanged(event.target.value)}/>
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={3}>
                                        <MuiCore.TextField fullWidth label='Account #'
                                                           error={!this.state.accountNumberValid}
                                                           value={this.state.accountNumber}
                                                           onChange={(event) => this.accountNumberChanged(event.target.value)}/>
                                    </MuiCore.Grid>

                                    <MuiCore.Grid item xs={12} sm={3}>
                                        <MuiCore.TextField fullWidth label='Verify Account #'
                                                           error={!this.state.accountNumber2Valid}
                                                           value={this.state.accountNumber2}
                                                           onChange={(event) => this.accountNumber2Changed(event.target.value)}/>
                                    </MuiCore.Grid>
                                    <MuiCore.Grid item xs={12} sm={4} style={{display: 'flex'}}>
                                        <MuiCore.FormControlLabel style={{whiteSpace: 'nowrap'}}
                                                                  control={<MuiCore.Checkbox
                                                                  onChange={(event) => this.setState({padSingleUse: event.target.checked})}
                                                                  checked={this.state.padSingleUse}/>}
                                                                  label="Add This Account for a Single Payment Only"/>
                                    </MuiCore.Grid>

                                </MuiCore.Hidden>
                            }
                            {
                                /* New Card selected.  New cards do not require verification so this
                                   takes a payment and the new card at the same time */
                            }
                            {!this.state.showReferralUrl &&
                                <MuiCore.Hidden xlDown={selectedPaymentMethodId !== 'CARD'}>
                                    <MuiCore.Grid item xs={12}>
                                        <Stripe.ElementsConsumer>
                                            {({elements, stripe}) => (
                                                <PaymentCardForm elements={elements}
                                                                 stripe={stripe}
                                                                 onValid={this.newCardValid}
                                                                 onInvalid={this.newCardInvalid}
                                                                 paymentProcessorId={this.props.paymentProcessorId}
                                                                 showAutoPayForm={this.showAutoPayPromptNewPaymentMethod}
                                                                 selectedAccount={this.state.selectedAccount}
                                                                 webUser={this.props.webUser}
                                                />
                                            )}
                                        </Stripe.ElementsConsumer>
                                    </MuiCore.Grid>
                                </MuiCore.Hidden>
                            }

                            {/* Hide the AutoPay Prompt until the button is clicked */}
                            {this.state.showAutoPayPromptWindow && !this.state.showReferralUrl &&
                                <AutoPayPrompt autoPayExisting={this.state.autoPayExisting}
                                               addAutoPay={this.state.addAutoPay}
                                               showAutoPayPromptWindow={this.state.showAutoPayPromptWindow}
                                               selectedAccount={this.state.selectedAccount}
                                               selectedPaymentMethod={this.state.selectedPaymentMethod}
                                               isNewPaymentMethod={this.state.showAutoPayPromptNewPaymentWindow}
                                               webUser={this.props.webUser}
                                               closeAutoPayActionSuccess={this.closeAutoPayActionSuccess}
                                               closeAutoPayActionDismiss={this.closeAutoPayActionDismiss}
                                />
                            }

                            {
                                /* Action buttons */
                            }
                            <MuiCore.Grid item xs={12} className={classes.paymentPanel}
                                          style={{textAlign: 'right'}}>
                                <MuiCore.Grid container spacing={1} justify='flex-end'>
                                    {(this.canEnableAutoPay() && !(["CARD", "ACH", "ACHC"].includes(this.state.selectedPaymentMethodId))) &&
                                        <MuiCore.Grid item>
                                            <MuiCore.Button
                                                disabled={!this.canEnableAutoPay() || this.state.showReferralUrl}
                                                onClick={this.showAutoPayPrompt}>
                                                Auto Pay
                                            </MuiCore.Button>
                                        </MuiCore.Grid>
                                    }
                                    <MuiCore.Grid item>
                                        <MuiCore.Button onClick={this.props.onLogout}>
                                            Logout
                                        </MuiCore.Button>
                                    </MuiCore.Grid>
                                    <MuiCore.Grid item>
                                        <MuiCore.Button disabled={!canSubmit || this.state.submitting}
                                                        onClick={this.submit}>
                                            {this.state.submitting ? 'Submitting...' : 'Submit'}
                                        </MuiCore.Button>
                                    </MuiCore.Grid>
                                </MuiCore.Grid>
                            </MuiCore.Grid>
                        </MuiCore.Grid>
                    </MuiCore.Paper>
                </Stripe.Elements>
            </React.Fragment>
        );
    }
}

export default withStyles(styles)(Payments);

